import React from "react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * Page frame for the public, logged-out survey flows (intake, pulse, team check-in,
 * heartbeat survey, pathways demo).
 *
 * @param {object} props
 * @param {string} [props.organizationName] Shown in the header next to the logo.
 * @param {string} [props.organizationLogo] Tenant logo URL.
 * @param {React.ReactNode} [props.banner] Full-width notice above the header (preview/demo modes).
 * @param {React.ReactNode} [props.progress] Sticky slot under the header, e.g. `<SurveyProgress />`.
 * @param {React.ReactNode} [props.footer="Powered by Atrium"]
 * @param {boolean} [props.panel=true] Wraps children in the centered surface panel. Pass false when
 *   the screen lays out its own cards (results pages).
 * @param {boolean} [props.center=false] Vertically centers the content (short screens).
 * @param {boolean} [props.dark=false] Forces the dark token scope regardless of the app theme.
 * @param {string} [props.className] Extra classes for the root.
 */
const PublicSurveyShell = ({
  organizationName,
  organizationLogo,
  banner,
  progress,
  footer = "Powered by Atrium",
  panel = true,
  center = false,
  dark = false,
  className,
  children,
}) => {
  const hasBrand = Boolean(organizationName || organizationLogo);

  return (
    <div
      className={cx(
        "flex min-h-[100dvh] flex-col bg-canvas text-fg",
        // color-scheme keeps native controls (date picker, scrollbars) in step with the tokens.
        dark ? "dark [color-scheme:dark]" : "dark:[color-scheme:dark]",
        className
      )}
    >
      {banner}

      {hasBrand && (
        <header className="px-4 pt-6 sm:pt-8">
          <div className="mx-auto flex w-full max-w-lg items-center justify-center gap-3">
            {organizationLogo && (
              <img
                src={organizationLogo}
                alt={organizationName ? "" : "Organization logo"}
                className="h-10 w-10 rounded-full border border-line bg-white object-contain p-1"
              />
            )}
            {organizationName && (
              <p className="text-sm font-semibold text-fg">{organizationName}</p>
            )}
          </div>
        </header>
      )}

      {progress && (
        <div className="sticky top-0 z-10 bg-canvas px-4 pb-2 pt-4">
          <div className="mx-auto w-full max-w-lg">{progress}</div>
        </div>
      )}

      <main
        className={cx(
          "flex flex-1 flex-col px-4 py-6 sm:py-8",
          center && "justify-center"
        )}
      >
        <div className="mx-auto w-full max-w-lg">
          {panel ? (
            <div className="rounded-card border border-line/60 bg-surface p-6 shadow-card dark:shadow-none sm:p-8">
              {children}
            </div>
          ) : (
            children
          )}
        </div>
      </main>

      {footer && (
        <footer className="px-4 pb-6 text-center text-xs text-fg-muted">{footer}</footer>
      )}
    </div>
  );
};

export default PublicSurveyShell;
